import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Stok kartında "Seri Paketleri" sekmesinin gövdesi.
// Verilen product_code grubundaki tüm seri paketlerini yönetir.
// Props: productCode, baseStockId (opsiyonel — yeni seri eklerken default kalemi öneri olarak sunabilir)
export default function StockSeriesManager({ productCode, baseStockId }) {
  const [list, setList] = useState([]);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | {} (new) | series object
  const [form, setForm] = useState({ code:'', name:'', barcode:'', notes:'', items:[] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/stocks/series', { params: { product_code: productCode } });
      if (r.data.success) setList(r.data.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [productCode]);

  const fetchVariants = useCallback(async () => {
    try {
      // Tüm stoklar arasından product_code eşleşenleri filtrele
      const r = await api.get('/stocks', { params: { search: productCode, limit: 200 } });
      if (r.data.success) {
        const all = r.data.data.items || [];
        setVariants(all.filter(s => s.product_code === productCode));
      }
    } catch (e) { console.error(e); }
  }, [productCode]);

  useEffect(() => { fetchList(); fetchVariants(); }, [fetchList, fetchVariants]);

  const startNew = () => {
    setEditing({});
    setForm({ code:'', name:'', barcode:'', notes:'', items: baseStockId ? [{ stock_id: baseStockId, quantity: 1 }] : [] });
    setErr('');
  };

  const startEdit = async (id) => {
    setErr('');
    try {
      const r = await api.get('/stocks/series/' + id);
      if (r.data.success) {
        const d = r.data.data;
        setEditing(d);
        setForm({
          code: d.code || '', name: d.name || '', barcode: d.barcode || '', notes: d.notes || '',
          items: (d.items || []).map(it => ({ stock_id: it.stock_id, quantity: it.quantity })),
        });
      }
    } catch (e) { setErr(e.response?.data?.message || e.message); }
  };

  const cancel = () => { setEditing(null); setErr(''); };

  const save = async () => {
    if (!form.name.trim()) { setErr('Seri adı zorunlu'); return; }
    if (form.items.length === 0) { setErr('En az bir kalem eklemelisiniz'); return; }
    setBusy(true); setErr('');
    try {
      const body = { product_code: productCode, ...form, items: form.items.filter(it => it.stock_id) };
      if (editing && editing.id) {
        await api.put('/stocks/series/' + editing.id, body);
      } else {
        await api.post('/stocks/series', body);
      }
      await fetchList();
      setEditing(null);
    } catch (e) { setErr(e.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Seri silinsin mi?')) return;
    try { await api.delete('/stocks/series/' + id); fetchList(); }
    catch (e) { setErr(e.response?.data?.message || e.message); }
  };

  const genBarcode = async () => {
    try {
      const r = await api.post('/stocks/next-barcode');
      if (r.data.success) setForm(p => ({ ...p, barcode: r.data.data.barcode }));
    } catch (e) { setErr(e.response?.data?.message || e.message); }
  };

  const addItemRow = () => setForm(p => ({ ...p, items: [...p.items, { stock_id: '', quantity: 1 }] }));
  const updateItem = (idx, key, val) => setForm(p => {
    const items = [...p.items];
    items[idx] = { ...items[idx], [key]: val };
    return { ...p, items };
  });
  const removeItem = (idx) => setForm(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const variantLabel = (v) => {
    const parts = [v.code];
    const cs = [v.color_name, v.size_name].filter(Boolean).join(' / ');
    if (cs) parts.push(cs);
    return parts.join(' — ');
  };

  if (editing) {
    return (
      <div className="form-grid" style={{ gap: 14 }}>
        {err && <div className="qlm-err" style={{ gridColumn: '1/-1' }}>{err}</div>}

        <div className="form-row">
          <label>Seri Kodu</label>
          <input value={form.code} onChange={e => setForm(p => ({...p, code: e.target.value}))} placeholder="Opsiyonel" />
        </div>
        <div className="form-row">
          <label>Seri Adı *</label>
          <input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Örn: Karışık Pantolon Paketi" />
        </div>
        <div className="form-row">
          <label>Barkod <button type="button" className="quick-add-btn" title="Otomatik üret" onClick={genBarcode}>+</button></label>
          <input value={form.barcode} onChange={e => setForm(p => ({...p, barcode: e.target.value}))} placeholder="Manuel girin veya + ile otomatik üretin" />
        </div>
        <div className="form-row full">
          <label>Notlar</label>
          <input value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} />
        </div>

        <div className="form-row full">
          <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Kalemler ({form.items.length})</span>
            <button type="button" className="btn-ghost" onClick={addItemRow} style={{ padding: '4px 12px', fontSize: '0.75rem' }}>+ Kalem Ekle</button>
          </label>
          <div className="qlm-list" style={{ marginTop: 6 }}>
            {form.items.length === 0 && <div className="qlm-empty">Kalem yok — "Kalem Ekle" ile renk/beden varyantı seçin</div>}
            {form.items.map((it, idx) => (
              <div key={idx} className="qlm-row" style={{ gridTemplateColumns: '1fr 100px auto' }}>
                <select value={it.stock_id || ''} onChange={e => updateItem(idx, 'stock_id', e.target.value)} className="qlm-input">
                  <option value="">Varyant seç…</option>
                  {variants.map(v => <option key={v.id} value={v.id}>{variantLabel(v)}</option>)}
                </select>
                <input type="number" min={0} step="0.001" value={it.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)}
                  className="qlm-input" style={{ textAlign: 'right' }} />
                <button className="qlm-btn qlm-del" onClick={() => removeItem(idx)} type="button">Sil</button>
              </div>
            ))}
          </div>
        </div>

        <div className="form-row full" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn-ghost" onClick={cancel} disabled={busy}>İptal</button>
          <button className="btn-primary-sm" onClick={save} disabled={busy}>{busy ? 'Kaydediliyor...' : 'Kaydet'}</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          Ürün Kodu: <strong style={{ color: 'var(--accent-primary)' }}>{productCode}</strong> — {list.length} seri
        </span>
        <button className="btn-primary-sm" onClick={startNew}>+ Yeni Seri</button>
      </div>

      {err && <div className="qlm-err">{err}</div>}

      <div className="qlm-list">
        {loading && <div className="qlm-empty">Yükleniyor...</div>}
        {!loading && list.length === 0 && <div className="qlm-empty">Henüz seri tanımlı değil</div>}
        {!loading && list.map(s => (
          <div key={s.id} className="qlm-row" style={{ gridTemplateColumns: '90px 1fr 130px auto auto' }}>
            <div className="qlm-code-text">{s.code || '—'}</div>
            <div className="qlm-name-text">{s.name}</div>
            <div className="qlm-extra-text" style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{s.barcode || '—'}</div>
            <button className="qlm-btn qlm-edit" onClick={() => startEdit(s.id)}>Düzenle</button>
            <button className="qlm-btn qlm-del" onClick={() => remove(s.id)}>Sil</button>
          </div>
        ))}
      </div>
    </div>
  );
}
